import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { graphql } from "gatsby";
import PropTypes from "prop-types";
import Styled from "@emotion/styled";
import { theme } from "../../utils/global-styles/theme";
import SingleColumnCard from "./child-components/SingleColumnCard";
import DoubleColumnCard from "./child-components/DualColumnCard";
import TripleColumnCard from "./child-components/TripleColumnCard";
import MobileCard from "./child-components/MobileCard";
import ThemeOne from "../shared/CourseIcons/ThemeOne";
import ThemeTwo from "../shared/CourseIcons/ThemeTwo";
import ThemeThree from "../shared/CourseIcons/ThemeThree";
import ThemeFour from "../shared/CourseIcons/ThemeFour";
import ThemeFive from "../shared/CourseIcons/ThemeFive";
import ThemeSix from "../shared/CourseIcons/ThemeSix";

const CatalogGrid = ({ searchCourses, courses, isNotCourseCatalogPage, isCourseDetailPage }) => {
  const catalogGridCards = isNotCourseCatalogPage ? courses : searchCourses;
  const ViewMore = Styled("p")`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2em 0;
    font-family: ${theme.fontFaces.help};
    font-weight: bold;
    &:hover {
      cursor: pointer;
    }
  `,
    [isMobileView, setMobileView] = useState(false);
  let rows = [],
    mobileIndex = 0;

  const StyledNoMatchesWrapper = Styled("div")`
      display: grid;
      grid-template: 1fr / 25% 75%;
      grid-area: 2 / 1 / span 1 / span 1;
      padding: 10em 0;
    `,
    StyledNoMatchesText = Styled("h2")`
      grid-area: 1 / 2 / span 1 / span 1;
      letter-spacing: inherit;
    `,
    determineIcon = courseType => {
      const theme =
        courseType.themeNumber;
      switch (theme) {
        case 1:
          return (
            <ThemeOne
              iconColorClassName={"path-main-fill"}
              width="35px"
              height="35px"
            />
          );
        case 2:
          return (
            <ThemeTwo
              iconColorClassName={"path-main-fill"}
              width="26px"
              height="29px"
            />
          );
        case 3:
          return (
            <ThemeThree
              iconColorClassName={"path-main-fill"}
              width="20.29px"
              height="34px"
            />
          );
        case 4:
          return (
            <ThemeFour
              iconColorClassName={"path-main-fill"}
              width="35px"
              height="35px"
            />
          );
        case 5:
          return (
            <ThemeFive
              iconColorClassName={"path-main-fill"}
              width="17.4px"
              height="35px"
            />
          );
        case 6:
          return (
            <ThemeSix
              iconColorClassName={"path-main-fill"}
              width="35px"
              height="35px"
            />
          );
        case undefined:
          return (
            <ThemeSix
              iconColorClassName={"path-main-fill"}
              width="30px"
              height="30px"
            />
          );
        default:
          // console.warn(
          //   `"${courseType}" is an invalid courseType passed into CourseCard.js. Check valid courseTypes in with the function determineIcon() in CourseCard.js.`
          // );
          return (
            <ThemeSix
              iconColorClassName={"path-main-fill"}
              width="30px"
              height="30px"
            />
          );
      }
    },
    /**
     * @param { Array }
     * Takes in courses in groups of 8 or less and creates grid
     * based on length of courses. -RJH 12/2019
     */
    calculateRows = coursesChunk => {
      switch (coursesChunk.length) {
        case 1:
          rows.push([coursesChunk[0], "1"]);
          break;
        case 2:
          rows.push([coursesChunk[0], coursesChunk[1], "2"]);
          break;
        case 3:
          rows.push(
            [coursesChunk[0], coursesChunk[1], "2"],
            [coursesChunk[2], "1"]
          );
          break;
        case 4:
          rows.push(
            [coursesChunk[0], coursesChunk[1], "2"],
            [coursesChunk[2], coursesChunk[3], "2R"]
          );
          break;
        case 5:
          rows.push(
            [coursesChunk[0], coursesChunk[1], "2"],
            [coursesChunk[2], "1"],
            [coursesChunk[3], coursesChunk[4], "2R"]
          );
          break;
        case 6:
          rows.push(
            [coursesChunk[0], coursesChunk[1], coursesChunk[2], "3"],
            [coursesChunk[3], coursesChunk[4], "2"],
            [coursesChunk[5], "1"]
          );
          break;
        case 7:
          rows.push(
            [coursesChunk[0], coursesChunk[1], coursesChunk[2], "3"],
            [coursesChunk[3], coursesChunk[4], "2"],
            [coursesChunk[5], coursesChunk[6], "2R"]
          );
          break;
        default:
          /**
           * If there are 8 courses, we build the full grid as represented by the
           * designs. -RJH 12/2019
           */
          rows.push(
            [coursesChunk[0], coursesChunk[1], coursesChunk[2], "3"],
            [coursesChunk[3], coursesChunk[4], "2"],
            [coursesChunk[5], "1"],
            [coursesChunk[6], coursesChunk[7], "2R"]
          );
          break;
      }
    },
    /**
     * @type { Array } splits courses into arrays of 8
     */
    chunks = catalogGridCards.reduce((resultChunk, currentItem, index) => {
      const chunkIndex = Math.floor(index / 8);
      if (!resultChunk[chunkIndex]) resultChunk[chunkIndex] = [];
      resultChunk[chunkIndex].push(currentItem);
      return resultChunk;
    }, []);

  /**
   * Take the individual arrays of 8 courses or less and return the rows
   */
  chunks.forEach(chunkArray => {
    calculateRows(chunkArray);
  });
  /**
   * @param { Array } courses
   * @param { layout } oneOf "3", "2", "1", "2R" - corresponds to card layouts:
   * "3" = row of 3 single cards
   * "2" = row of 1 single-column card followed by a two-column card
   * "1" = row of 1 triple-column card
   * "2R" = row of 1 double-column card followed by a single-column card
   * @param { index } number
   */
  const createRow = (courses, layout, index) => {
      const StyledRow = Styled("div")`
        display: grid;
        grid-area: ${index + 1} / 1 / span 1 / span 3;
        grid-template-columns: repeat(3, 33.33%);
      `;
      // Mobile Layout Starts
      if (isMobileView) {
        return courses.map((course, courseIndex) => {
          if (typeof course !== "string") {
            mobileIndex++;
            return (
              <MobileCard
                {...course}
                backgroundColorClass={
                  courseIndex & 1
                    ? "course-catalog-light-grey-single-card"
                    : "course-catalog-primary-single-card"
                }
                courseIcon={determineIcon(course.theme)}
                courseIndex={mobileIndex}
                key={courseIndex}
              />
            );
          }
          return null;
        });
        // Mobile Layout Ends
      } else {
        switch (layout) {
          case "3":
            return (
              <StyledRow key={index}>
                <SingleColumnCard
                  {...courses[0]}
                  gridColumnValue={"1"}
                  backgroundColorClass={"course-catalog-primary-single-card"}
                  courseIcon={determineIcon(courses[0].theme)}
                />
                <SingleColumnCard
                  {...courses[1]}
                  gridColumnValue={"2"}
                  backgroundColorClass={"course-catalog-light-grey-single-card"}
                  courseIcon={determineIcon(courses[1].theme)}
                />
                <SingleColumnCard
                  {...courses[2]}
                  gridColumnValue={"3"}
                  backgroundColorClass={"course-catalog-primary-single-card"}
                  courseIcon={determineIcon(courses[2].theme)}
                />
              </StyledRow>
            );
          case "2":
            return (
              <StyledRow key={index}>
                <SingleColumnCard
                  {...courses[0]}
                  gridColumnValue={"1"}
                  backgroundColorClass={"course-catalog-light-grey-single-card"}
                  courseIcon={determineIcon(courses[0].theme)}
                />
                <DoubleColumnCard
                  {...courses[1]}
                  gridColumnValue={"2"}
                  backgroundColorClass={"course-catalog-primary-single-card"}
                  courseIcon={determineIcon(courses[1].theme)}
                  isCourseDetailPage={isCourseDetailPage}
                />
              </StyledRow>
            );
          case "1":
            return (
              <TripleColumnCard
                key={index}
                {...courses[0]}
                gridRowValue={(index + 1).toString()}
                gridColumnValue={"1"}
                backgroundColorClass={"course-catalog-primary-single-card"}
                courseIcon={determineIcon(courses[0].theme)}
                isCourseDetailPage={isCourseDetailPage}
              />
            );
          //This default case is always going to be "2R" (see head comments of this function)
          default:
            return (
              <StyledRow key={index}>
                <DoubleColumnCard
                  {...courses[0]}
                  gridColumnValue={"1"}
                  backgroundColorClass={"course-catalog-primary-single-card"}
                  courseIcon={determineIcon(courses[0].theme)}
                  isCourseDetailPage={isCourseDetailPage}
                />
                <SingleColumnCard
                  {...courses[1]}
                  gridColumnValue={"3"}
                  backgroundColorClass={"course-catalog-light-grey-single-card"}
                  courseIcon={determineIcon(courses[1].theme)}
                />
              </StyledRow>
            );
        }
      }
    },
    /**
     * This will run on window resize and when the page
     * loads to determine if we are using the mobile layout
     * or the full grid layout -RJH 12/2019
     */
    handleResize = () => {
      setMobileView(window.innerWidth < 1201);
    };

  // Sets up window resize handler
  useEffect(() => {
    // If executed server-side (no window object) it will return false(exit) out of the useEffect() hook
    if (typeof window !== "object") return false;
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [searchCourses, courses]);

  /**
   * This is where this component actually starts rendering. If there are rows to render,
   * we map over them and create our rows. Otherwise, we render a simple message. -RJH 12/2019
   */
  // searchCourses[0].theme && rows.length
  return catalogGridCards.length && catalogGridCards[0].theme && rows.length ? (
    <>
      {rows.map((row, index) => {
        return createRow(row, row[row.length - 1], index);
      })}
      {/* This pagination was removed as per JT & John Mayo Smith's recommendation */}
      {/* {numberOfCoursesLoaded <
        (searchCourses ? searchCourses.length : courses.length) && (
        <ViewMore
          className="primary secondary-hover"
          onClick={() => {
            if (courses.length < numberOfCoursesLoaded + 8) {
              setNumberOfCoursesLoaded(courses.length);
            } else {
              setNumberOfCoursesLoaded(numberOfCoursesLoaded + 8);
            }
          }}
        >
          View More
        </ViewMore>
      )} */}
    </>
  ) : (
    <StyledNoMatchesWrapper>
      <StyledNoMatchesText>No matches found.</StyledNoMatchesText>
    </StyledNoMatchesWrapper>
  );
};

CatalogGrid.propTypes = {
  searchCourses: PropTypes.arrayOf(
    PropTypes.shape({
      courseId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
      shortDescription: PropTypes.shape({ shortDescription: PropTypes.string }),
      courseCost: PropTypes.string,
      courseDuration: PropTypes.string,
      courseType: PropTypes.string,
      courseAvailability: PropTypes.string,
      courseImage: PropTypes.shape({
        courseImage: PropTypes.shape({
          fluid: PropTypes.shape({ srcSet: PropTypes.string }),
          description: PropTypes.string,
        }),
      }),
      theme: PropTypes.shape({
        theme: PropTypes.string,
      }),
    })
  ),
  courses: PropTypes.arrayOf(
    PropTypes.shape({
      courseId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
      shortDescription: PropTypes.shape({ shortDescription: PropTypes.string }),
      courseCost: PropTypes.string,
      courseDuration: PropTypes.string,
      courseType: PropTypes.string,
      courseAvailability: PropTypes.string,
      courseImage: PropTypes.shape({
        courseImage: PropTypes.shape({
          fluid: PropTypes.shape({ srcSet: PropTypes.string }),
          description: PropTypes.string,
        }),
      }),
      theme: PropTypes.shape({
        theme: PropTypes.string,
      }),
    })
  ),
  isNotCourseCatalogPage: PropTypes.bool,
};

export const query = graphql`
  fragment CourseCard on ContentfulCourseDetailPage {
    languageCode
    courseId
    slug
    courseImage {
      gatsbyImageData
      description
    }
    courseId
    courseType
    title
    shortDescription {
      shortDescription
    }
    darkImageOverlayText
    courseCost
    courseCostInOtherCurrencies {
      usd
      pound
      euro
    }
    courseAvailability
    courseDuration
    courseDescription {
      courseDescription
    }
    theme: theme20 {
      theme
      themeNumber
    }
  }
`;

export default connect(
  state => ({
    searchCourses: state.courseReducer.searchCourses,
  }),
  null
)(CatalogGrid);
